import { useEffect, useRef, useState, useMemo } from 'react'
import { visionAPI } from '../lib/api'
import { useVisionSocket, useStageAudioSync } from '../lib/visionSocket'
import { Volume2, Trophy } from 'lucide-react'
import TeamMark, { parseFlag } from '@/components/TeamMark'

/* ================================================================
   Kempenvision Stage — beamer view
   Polls /vision/state every 1s. Renders 3 views: idle / song / ceremony.
   Audio-reactive Canvas 2D visualizer with 6 genre presets.
   Ported from the LANSONG mockup (see Artifact d0881479).
================================================================ */

/* Genre-kleuren als concrete hex (niet CSS var(--...))— Canvas 2D's
   addColorStop kan geen custom properties parsen; die geven silent errors
   en de visualisatie stopt. Waarden gespiegeld uit stage-tokens.css. */
const GENRES = {
  edm:       { name: 'EDM',       accent: '#ff2b8f', accent2: '#00d9ff', bpm: 128 },
  rock:      { name: 'Rock',      accent: '#ff4a1f', accent2: '#ffb700', bpm: 92  },
  pop:       { name: 'Pop',       accent: '#ff7ac8', accent2: '#a76bff', bpm: 118 },
  ballad:    { name: 'Ballad',    accent: '#ffcf40', accent2: '#7ba6ff', bpm: 62  },
  hiphop:    { name: 'Hiphop',    accent: '#c8ff2b', accent2: '#ff2bc8', bpm: 84  },
  synthwave: { name: 'Synthwave', accent: '#ff2b8f', accent2: '#8b3fff', bpm: 108 },
}

const POINT_STEPS = [1,2,3,4,5,6,7,8,10,12]
const POLL_MS = 1000


/* ============ Font loader ============ */
function useStageFonts() {
  useEffect(() => {
    const l = document.createElement('link')
    l.rel = 'stylesheet'
    // Lokaal bestand: de beamer moet ook zonder internet het juiste lettertype tonen.
    l.href = '/fonts/stage-fonts.css'
    document.head.appendChild(l)
    return () => document.head.removeChild(l)
  }, [])
}

/* ================================================================
   PAGE
================================================================ */
export default function KempenvisionStagePage() {
  useStageFonts()
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [connected, setConnected] = useState(false)
  // OBS-Browser-Source geeft geen user-gesture, dus de klik-om-te-starten
  // overlay blijft eeuwig staan. Truc: URL-param `?autoplay=1` (of `obs=1`)
  // slaat de overlay over. OBS gebruikt intern Chromium met de flag
  // `--autoplay-policy=no-user-gesture-required`, dus audio kan spelen
  // zonder klik. Voor gewone browsers blijft de overlay bestaan zodat
  // autoplay-blokkering niet stilletjes faalt.
  const autoplayParam = typeof window !== 'undefined'
    && /[?&](autoplay|obs)=1(&|$)/.test(window.location.search)
  const [audioReady, setAudioReady] = useState(autoplayParam)

  // Chrome autoplay policy: audio kan niet spelen zonder user gesture op deze tab.
  // We doen twee dingen bij de eerste klik:
  //   1. AudioContext aanmaken + resume (voor de visualizer FFT-analyzer)
  //   2. Een dummy `<audio>` .play() aanroepen op een tiny silent WAV zodat
  //      alle latere audio-elementen op deze tab ook mogen spelen (zonder
  //      deze step werkt WinnerAudio niet, want die was niet gemount toen de
  //      user klikte — Chrome kent per-element policy).
  const initAudio = async () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext
      if (AC) {
        const ac = new AC()
        if (ac.state === 'suspended') await ac.resume()
        const buf = ac.createBuffer(1, 1, 22050)
        const src = ac.createBufferSource()
        src.buffer = buf
        src.connect(ac.destination)
        src.start(0)
      }
    } catch (e) { /* ignore */ }
    try {
      // Silent 1-frame WAV — 44 bytes header + 4 bytes stille sample.
      const silentWav = 'data:audio/wav;base64,UklGRiwAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQgAAAAAAAAAAAAAAA=='
      const a = new Audio(silentWav)
      a.volume = 0
      await a.play().catch(() => {})
    } catch (e) { /* ignore */ }
    setAudioReady(true)
  }

  // Bij ?autoplay=1 (OBS-modus) alsnog éénmaal initAudio() draaien om de
  // AudioContext + visualizer klaar te zetten. OBS start Chromium met
  // `--autoplay-policy=no-user-gesture-required` waardoor .play() slaagt
  // zonder klik.
  useEffect(() => {
    if (autoplayParam) initAudio().catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const reloadRef = useRef(null)
  useEffect(() => {
    let alive = true
    const poll = async () => {
      try {
        const { data } = await visionAPI.getState()
        if (alive) { setData(data); setError(null); setConnected(true) }
      } catch (e) { if (alive) { setError(e.message); setConnected(false) } }
    }
    reloadRef.current = poll
    poll()
    // Snelle safety-poll voor als de socket down is (bv. flaky wifi op de beamer-PC).
    // Bij werkende socket is dit meestal een no-op, de refresh gebeurt via useVisionSocket.
    const iv = setInterval(poll, POLL_MS)
    return () => { alive = false; clearInterval(iv); reloadRef.current = null }
  }, [])
  useVisionSocket(() => reloadRef.current?.())

  if (!data) return <StageChrome><IdleScreen msg="Verbinden…" /></StageChrome>
  if (!data.show) return <StageChrome><IdleScreen msg="Geen actieve Kempenvision show" /></StageChrome>

  const view = data.state?.current_view ?? 'idle'
  return (
    <StageChrome connected={connected}>
      {!audioReady && (
        <div
          onClick={initAudio}
          className="fixed inset-0 z-[100] flex items-center justify-center cursor-pointer"
          style={{ background: 'rgba(11,5,24,.92)', backdropFilter: 'blur(8px)' }}
        >
          <div className="text-center p-8 max-w-md">
            <div className="mb-4 flex justify-center"><Volume2 className="h-16 w-16" strokeWidth={1.5} /></div>
            <h2 style={{
              fontFamily: "'Big Shoulders Display', Impact, sans-serif",
              fontWeight: 900, fontSize: 48, textTransform: 'uppercase',
              letterSpacing: '-.01em', color: '#f0e8ff', margin: 0,
            }}>Klik om te starten</h2>
            <p className="mt-3 text-sm" style={{ color: '#c9bde3' }}>
              Browsers eisen een klik voordat ze audio mogen afspelen.
              Klik ergens op dit scherm om de show te starten.
            </p>
          </div>
        </div>
      )}
      {view === 'idle'     && <IdleScreen show={data.show} standings={data.standings} />}
      {view === 'song'     && <SongView data={data} />}
      {view === 'ceremony' && <CeremonyView data={data} />}
      {view === 'results'  && <ResultsView data={data} />}

      {/* Winner-audio zit BUITEN de view-switch zodat de <audio>-tag altijd
          gemount blijft. Chrome eist een user-gesture per audio-element; als
          we hem alleen bij view='results' zouden mounten kwamen ze telkens
          in een nieuwe policy-context terecht. Nu erft hij de gesture van
          initAudio en kan hij spelen zodra control-page audio_playing zet. */}
      {view === 'results' && data.current_entry?.audio_url && (() => {
        const winnerId = data.standings?.[0]?.entry_id
        const shouldPlay = !!data.state?.audio_playing
          && data.state?.current_entry_id === winnerId
          && audioReady
        return (
          <WinnerAudio url={data.current_entry.audio_url} shouldPlay={shouldPlay} />
        )
      })()}
    </StageChrome>
  )
}

/* ============ Chrome ============ */
function StageChrome({ children, connected }) {
  return (
    <div className="fixed inset-0 overflow-hidden" style={{
      background: 'var(--stage-bg)',
      color: '#f0e8ff',
      fontFamily: "'Manrope', system-ui, sans-serif",
    }}>
      {children}
      {connected === false && (
        <div className="absolute top-3 right-3 text-xs px-2 py-1 rounded"
             style={{ background: '#ff3b3b', color: 'white' }}>
          ⚠ verbinding weg
        </div>
      )}
    </div>
  )
}

/* ================================================================
   IDLE SCREEN
================================================================ */
function IdleScreen({ show, standings, msg }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center gap-6 p-12 text-center">
      <div className="h-24 w-24 rounded-2xl flex items-center justify-center shadow-2xl"
           style={{ background: 'linear-gradient(135deg,var(--stage-hot),var(--stage-purple))', boxShadow: '0 0 60px rgba(255,43,143,.4)' }}>
        <svg viewBox="0 0 24 24" width="48" height="48" fill="white">
          <circle cx="12" cy="12" r="2"/>
          <path d="M4.9 4.9C3.1 6.7 2 9.2 2 12s1.1 5.3 2.9 7.1M7.8 7.8C6.6 9 5.9 10.4 5.9 12s.7 3 1.9 4.2M19.1 4.9C20.9 6.7 22 9.2 22 12s-1.1 5.3-2.9 7.1M16.2 7.8C17.4 9 18.1 10.4 18.1 12s-.7 3-1.9 4.2" stroke="white" strokeWidth="2" fill="none"/>
        </svg>
      </div>
      <div>
        <h1 style={{
          fontFamily: "'Big Shoulders Display', Impact, sans-serif",
          fontWeight: 900, fontSize: 'clamp(48px, 8vw, 120px)',
          textTransform: 'uppercase', letterSpacing: '-0.01em', lineHeight: 0.95,
        }}>
          {show?.name ?? 'Kempenvision'}
        </h1>
        {show?.edition && (
          <div style={{
            fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 700,
            fontSize: 'clamp(24px, 3vw, 48px)', color: 'var(--stage-cold)', letterSpacing: '.06em',
          }}>{show.edition}</div>
        )}
        {show?.subtitle && <div className="text-lg mt-2" style={{ color: '#8b7fa8' }}>{show.subtitle}</div>}
      </div>
      {msg && <div className="text-xl" style={{ color: '#8b7fa8' }}>{msg}</div>}
    </div>
  )
}

function MiniStandings({ standings }) {
  return (
    <div className="space-y-1">
      {standings.map((s, i) => {
        const flag = parseFlag(s.team_flag_colors)
        return (
          <div key={s.entry_id} className="flex items-center gap-3 p-3 rounded" style={{ background: '#1a0f36' }}>
            <span className="w-8 text-center font-mono font-bold" style={{ color: i === 0 ? 'var(--stage-gold)' : '#c9bde3' }}>{i+1}</span>
            <TeamMark flag={flag} logo={s.team_logo_url} size="md" />
            <span className="flex-1 text-left font-semibold" style={{
              fontFamily: "'Big Shoulders Display', sans-serif", fontSize: 20, letterSpacing: '.02em', textTransform: 'uppercase',
            }}>{s.team_name}</span>
            <span className="font-mono font-bold text-xl" style={{ fontVariantNumeric: 'tabular-nums', color: '#f0e8ff' }}>{s.total_points}</span>
          </div>
        )
      })}
    </div>
  )
}


/* ================================================================
   SONG VIEW — canvas viz + overlay + audio
================================================================ */
function SongView({ data }) {
  const entry = data.current_entry
  const genreKey = data.state?.genre_override || entry?.genre || 'edm'
  const g = GENRES[genreKey] ?? GENRES.edm
  const shouldPlay = !!data.state?.audio_playing
  const audioUrl = entry?.audio_url

  return (
    <div className="h-full w-full relative">
      <Visualizer genreKey={genreKey} audioUrl={audioUrl} shouldPlay={shouldPlay} bpmHint={entry?.bpm ?? g.bpm} />
      {/* Song overlay */}
      <div className="absolute inset-x-0 bottom-0 p-12 pointer-events-none"
           style={{ background: 'linear-gradient(180deg, transparent, rgba(11,5,24,.85))' }}>
        <div className="flex items-end justify-between gap-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-4 rounded"
                 style={{ background: g.accent, color: 'white', fontWeight: 700, fontSize: 12, letterSpacing: '.2em', textTransform: 'uppercase' }}>
              <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 999, background: 'white' }} />
              {g.name}
            </div>
            <h1 style={{
              fontFamily: "'Big Shoulders Display', Impact, sans-serif",
              fontWeight: 900, fontSize: 'clamp(48px, 8vw, 128px)', lineHeight: 0.9,
              textTransform: 'uppercase', letterSpacing: '-.01em', margin: 0,
              color: '#f0e8ff', textWrap: 'balance',
            }}>
              {entry?.title || '—'}
            </h1>
            <div className="mt-3 text-xl" style={{ color: '#c9bde3', fontWeight: 500 }}>
              <TeamMark flag={parseFlag(entry?.team_flag_colors)} logo={entry?.team_logo_url} size="sm" />
              <span className="inline-block align-middle ml-3">{entry?.team_name}</span>
              {entry?.artist_label && <><span className="mx-3" style={{ color: '#8b7fa8' }}>·</span>{entry.artist_label}</>}
              {entry?.bpm && <><span className="mx-3" style={{ color: '#8b7fa8' }}>·</span>{entry.bpm} BPM</>}
              <span className="mx-3" style={{ color: '#8b7fa8' }}>·</span>Entry #{entry?.running_order}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ================================================================
   VISUALIZER — Canvas 2D + Web Audio, 6 genre renderers
================================================================ */
function Visualizer({ genreKey, audioUrl, shouldPlay, bpmHint }) {
  const canvasRef = useRef(null)
  const audioRef  = useRef(null)
  const ctxRef    = useRef({ audioCtx: null, analyser: null, fft: null, wave: null, source: null, lastUrl: null })
  const stateRef  = useRef({ genreKey, shouldPlay, bpmHint, t: 0, lastT: performance.now() })
  stateRef.current.genreKey = genreKey
  stateRef.current.shouldPlay = shouldPlay
  stateRef.current.bpmHint = bpmHint

  // Realtime audio-sync met control page
  const { emitTick } = useStageAudioSync({
    onSeek: ({ time }) => {
      if (audioRef.current && Number.isFinite(time)) {
        audioRef.current.currentTime = Math.max(0, Math.min(time, audioRef.current.duration || 0))
      }
    },
  })
  useEffect(() => {
    const el = audioRef.current
    if (!el) return
    const tick = () => emitTick({
      position: el.currentTime || 0,
      duration: el.duration || 0,
      playing: !el.paused,
    })
    const onLoaded = () => tick()
    el.addEventListener('loadedmetadata', onLoaded)
    el.addEventListener('play', tick)
    el.addEventListener('pause', tick)
    el.addEventListener('seeked', tick)
    const iv = setInterval(() => { if (!el.paused) tick() }, 1000)
    return () => {
      clearInterval(iv)
      el.removeEventListener('loadedmetadata', onLoaded)
      el.removeEventListener('play', tick)
      el.removeEventListener('pause', tick)
      el.removeEventListener('seeked', tick)
    }
  }, [audioUrl])

  // Setup analyser when audio element mounts
  useEffect(() => {
    if (!audioRef.current) return
    const ac = ctxRef.current
    if (!ac.audioCtx) {
      try {
        const AC = window.AudioContext || window.webkitAudioContext
        ac.audioCtx = new AC()
        ac.analyser = ac.audioCtx.createAnalyser()
        ac.analyser.fftSize = 1024
        ac.analyser.smoothingTimeConstant = 0.75
        ac.fft = new Uint8Array(ac.analyser.frequencyBinCount)
        ac.wave = new Uint8Array(ac.analyser.frequencyBinCount)
      } catch (e) { console.warn('AudioContext niet beschikbaar', e) }
    }
    if (ac.audioCtx && audioRef.current && !ac.source) {
      try {
        ac.source = ac.audioCtx.createMediaElementSource(audioRef.current)
        ac.source.connect(ac.analyser)
        ac.analyser.connect(ac.audioCtx.destination)
      } catch (e) { /* already connected */ }
    }
  }, [audioUrl])

  // Reset audio src when url changes
  useEffect(() => {
    if (!audioRef.current) return
    if (audioUrl && audioRef.current.src !== audioUrl) {
      audioRef.current.src = audioUrl
      audioRef.current.load()
    }
  }, [audioUrl])

  // Play/pause based on shouldPlay
  useEffect(() => {
    if (!audioRef.current || !audioUrl) return
    const ac = ctxRef.current
    if (shouldPlay) {
      ac.audioCtx?.resume().catch(() => {})
      audioRef.current.play().catch(() => {})
    } else {
      audioRef.current.pause()
    }
  }, [shouldPlay, audioUrl])

  // Render loop
  useEffect(() => {
    let raf, dpr
    const canvas = canvasRef.current
    const cctx = canvas.getContext('2d')
    dpr = Math.min(2, window.devicePixelRatio || 1)
    const fit = () => {
      const r = canvas.getBoundingClientRect()
      canvas.width  = Math.max(2, Math.floor(r.width  * dpr))
      canvas.height = Math.max(2, Math.floor(r.height * dpr))
    }
    fit()
    const ro = new ResizeObserver(fit)
    ro.observe(canvas)

    const loop = (now) => {
      const s = stateRef.current
      const dt = Math.min(0.05, (now - s.lastT) / 1000)
      s.lastT = now
      s.t += dt
      const g = GENRES[s.genreKey] ?? GENRES.edm
      const ac = ctxRef.current
      let fft, wave
      const hasRealAudio = ac.analyser && audioRef.current && !audioRef.current.paused && audioRef.current.currentTime > 0
      if (hasRealAudio) {
        ac.analyser.getByteFrequencyData(ac.fft)
        ac.analyser.getByteTimeDomainData(ac.wave)
        fft = ac.fft; wave = ac.wave
      } else {
        fft = fakeFft(s.t, s.bpmHint || g.bpm)
        wave = fakeWave(s.t, s.bpmHint || g.bpm)
      }
      const bass = bandLevel(fft, 0, 12)
      const mid  = bandLevel(fft, 12, 80)
      const hi   = bandLevel(fft, 80, 300)
      const w = canvas.width, h = canvas.height
      cctx.setTransform(1,0,0,1,0,0)
      cctx.clearRect(0,0,w,h)
      const R = RENDERERS[s.genreKey] ?? RENDERERS.edm
      R(cctx, w, h, fft, wave, {bass, mid, hi}, s.t, g, dpr)
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => { cancelAnimationFrame(raf); ro.disconnect() }
  }, [])

  return (
    <>
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{ background: '#000' }} />
      <audio ref={audioRef} crossOrigin="anonymous" preload="auto" />
    </>
  )
}

/* ============ Fake audio data (silent fallback) ============ */
function fakeFft(t, bpm) {
  const n = 512, arr = new Uint8Array(n)
  const beat = 60 / (bpm || 120)
  const bphase = (t % beat) / beat
  const kick = Math.exp(-bphase * 6)
  const sph = ((t + beat/2) % beat) / beat
  const snare = Math.exp(-sph * 12)
  for (let i = 0; i < n; i++) {
    const rel = i / n
    let v = 0
    if (rel < 0.06) v = kick * 220 + Math.sin(t*2 + i*0.6)*20 + 40
    else if (rel < 0.2) v = 60 + Math.sin(t*3 + i*0.5)*30 + kick*40
    else if (rel < 0.5) v = 60 + Math.sin(t*5 + i*0.3)*40 + snare*70
    else v = 40 + Math.random()*45 + snare*30
    arr[i] = Math.max(0, Math.min(255, v * (1 - rel*0.4)))
  }
  return arr
}
function fakeWave(t, bpm) {
  const n = 512, arr = new Uint8Array(n)
  const beat = 60 / (bpm || 120)
  const bphase = (t % beat) / beat
  const kick = Math.exp(-bphase * 6)
  for (let i = 0; i < n; i++) {
    const p = i / n
    const v = 128 + Math.sin(p*Math.PI*2*3 + t*4)*32*(0.4+kick*0.6)
                  + Math.sin(p*Math.PI*2*11 + t*7)*12
                  + (Math.random()-0.5)*10
    arr[i] = Math.max(0, Math.min(255, v))
  }
  return arr
}
function bandLevel(fft, a, b) {
  let s = 0, c = 0
  for (let i = a; i < b && i < fft.length; i++) { s += fft[i]; c++ }
  return c ? s / c / 255 : 0
}

/* ============ 6 Renderers ============ */
const RENDERERS = {
  edm(ctx, w, h, fft, wave, {bass, mid, hi}, t, g, dpr) {
    ctx.fillStyle = 'rgba(11,5,24,.35)'; ctx.fillRect(0,0,w,h)
    const cx = w/2, cy = h/2, kick = 1 + bass*3
    for (let i = 0; i < 5; i++) {
      const r = kick * (60 + i*70) * dpr + Math.sin(t*2+i)*10*dpr
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI*2)
      ctx.strokeStyle = i%2 ? g.accent : g.accent2
      ctx.lineWidth = (3 - i*0.4) * dpr; ctx.globalAlpha = 0.7 - i*0.12; ctx.stroke()
    }
    ctx.globalAlpha = 1
    const n = 80
    for (let i = 0; i < n; i++) {
      const ang = i * (Math.PI*2/n) + t * (0.3 + bass)
      const rad = 220*dpr + Math.sin(t*4+i)*40*dpr + fft[i*4]*0.7*dpr
      const x = cx + Math.cos(ang) * rad, y = cy + Math.sin(ang) * rad
      ctx.fillStyle = i%2 ? g.accent : g.accent2
      ctx.globalAlpha = 0.4 + hi*0.6
      ctx.beginPath(); ctx.arc(x, y, (2 + bass*4)*dpr, 0, Math.PI*2); ctx.fill()
    }
    ctx.globalAlpha = 1
    const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, 200*dpr*(1+bass))
    grd.addColorStop(0, g.accent + 'cc'); grd.addColorStop(1, 'transparent')
    ctx.fillStyle = grd; ctx.fillRect(0,0,w,h)
  },
  rock(ctx, w, h, fft, wave, {bass, mid, hi}, t, g, dpr) {
    ctx.fillStyle = 'rgba(20,4,4,.3)'; ctx.fillRect(0,0,w,h)
    for (let i = 0; i < 40; i++) {
      const seed = (i*97 + Math.floor(t*20))%100
      const x = (seed/100)*w, y = h - ((t*80 + i*30 + seed) % h)
      ctx.fillStyle = seed%2 ? g.accent : g.accent2
      ctx.globalAlpha = 0.15 + hi*0.6
      ctx.beginPath(); ctx.arc(x, y, (1+(seed%4))*dpr, 0, Math.PI*2); ctx.fill()
    }
    ctx.globalAlpha = 1
    const cy = h/2, step = Math.max(2*dpr, w/300)
    ctx.lineWidth = 2*dpr; ctx.strokeStyle = g.accent; ctx.beginPath()
    for (let x = 0, i = 0; x < w; x += step, i++) {
      const v = (wave[i*3 % wave.length] - 128) / 128
      const y = cy + v * h*0.4 * (1 + bass)
      if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y)
    }
    ctx.stroke()
    ctx.strokeStyle = g.accent2; ctx.globalAlpha = 0.5; ctx.beginPath()
    for (let x = 0, i = 0; x < w; x += step, i++) {
      const v = (wave[i*3 % wave.length] - 128) / 128
      const y = cy - v * h*0.4 * (1 + bass)
      if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y)
    }
    ctx.stroke(); ctx.globalAlpha = 1
  },
  pop(ctx, w, h, fft, wave, {bass, mid, hi}, t, g, dpr) {
    ctx.fillStyle = 'rgba(30,10,40,.28)'; ctx.fillRect(0,0,w,h)
    for (let i = 0; i < 60; i++) {
      const seed = (i*137.5) % 360
      const x = (Math.sin(t*0.4 + i)*.5 + .5) * w
      const y = h - ((t * (40 + (i*13)%50) + seed*2) % (h + 100))
      const r = (10 + (i%6)*4 + bass*30) * dpr
      ctx.fillStyle = i%3 === 0 ? g.accent : (i%3 === 1 ? g.accent2 : '#fff')
      ctx.globalAlpha = 0.10 + mid*0.35
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI*2); ctx.fill()
      ctx.globalAlpha = 0.4; ctx.fillStyle = '#fff'
      ctx.beginPath(); ctx.arc(x - r*0.3, y - r*0.3, r*0.25, 0, Math.PI*2); ctx.fill()
    }
    ctx.globalAlpha = 1
  },
  ballad(ctx, w, h, fft, wave, {bass, mid, hi}, t, g, dpr) {
    ctx.fillStyle = 'rgba(6,8,20,.4)'; ctx.fillRect(0,0,w,h)
    for (let i = 0; i < 200; i++) {
      const x = (i*137.5) % w, y = (i*89.3) % h
      const tw = Math.sin(t + i*0.7)*.5 + .5
      ctx.fillStyle = i%5 === 0 ? g.accent : '#fff'
      ctx.globalAlpha = 0.15 + tw*0.5 + hi*0.2
      ctx.beginPath(); ctx.arc(x, y, (1+(i%3)*0.5)*dpr, 0, Math.PI*2); ctx.fill()
    }
    ctx.globalAlpha = 1
    const cx = w*0.7, cy = h*0.55, r = (140 + bass*80 + mid*40) * dpr
    const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, r)
    grd.addColorStop(0, g.accent + 'e6'); grd.addColorStop(.5, g.accent + '33'); grd.addColorStop(1, 'transparent')
    ctx.fillStyle = grd; ctx.fillRect(0,0,w,h)
    ctx.fillStyle = g.accent2 + '22'; ctx.fillRect(0, h*0.75, w, h*0.25)
  },
  hiphop(ctx, w, h, fft, wave, {bass, mid, hi}, t, g, dpr) {
    ctx.fillStyle = 'rgba(10,20,4,.35)'; ctx.fillRect(0,0,w,h)
    const rows = 14, cols = 22, cellW = w/cols, cellH = h/rows
    ctx.lineWidth = 1*dpr; ctx.strokeStyle = g.accent + '66'
    for (let r = 0; r <= rows; r++) { const y = r*cellH; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke() }
    for (let c = 0; c <= cols; c++) { const x = c*cellW; ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke() }
    for (let c = 0; c < cols; c++) {
      const v = fft[c*4]/255
      const bh = v * h*0.6 * (1 + bass*0.4)
      ctx.fillStyle = c%2 ? g.accent : g.accent2
      ctx.globalAlpha = 0.7
      ctx.fillRect(c*cellW + cellW*0.2, h - bh, cellW*0.6, bh)
    }
    ctx.globalAlpha = 1
    if (bass > 0.55) {
      ctx.strokeStyle = g.accent; ctx.lineWidth = 4*dpr*bass
      ctx.beginPath(); ctx.arc(w/2, h/2, 60*dpr + bass*180*dpr, 0, Math.PI*2); ctx.stroke()
    }
  },
  synthwave(ctx, w, h, fft, wave, {bass, mid, hi}, t, g, dpr) {
    const sky = ctx.createLinearGradient(0, 0, 0, h*0.6)
    sky.addColorStop(0, '#0a041a'); sky.addColorStop(1, '#2a0a4a')
    ctx.fillStyle = sky; ctx.fillRect(0,0,w,h*0.6)
    const cx = w/2, cy = h*0.55, sunR = (120 + bass*60) * dpr
    const sg = ctx.createRadialGradient(cx, cy, 0, cx, cy, sunR)
    sg.addColorStop(0, g.accent); sg.addColorStop(.7, g.accent2); sg.addColorStop(1, 'transparent')
    ctx.fillStyle = sg; ctx.beginPath(); ctx.arc(cx, cy, sunR, 0, Math.PI*2); ctx.fill()
    ctx.fillStyle = '#0a041a'
    for (let i = 0; i < 6; i++) {
      const y = cy - sunR*0.6 + i*sunR*0.24 - (t*30) % (sunR*0.24)
      ctx.fillRect(cx - sunR, y, sunR*2, 4*dpr)
    }
    ctx.fillStyle = '#03000a'; ctx.fillRect(0, h*0.6, w, h*0.4)
    ctx.strokeStyle = g.accent2; ctx.lineWidth = 1.5*dpr
    const hy = h*0.6, vp = cx
    for (let i = -20; i <= 20; i++) {
      ctx.beginPath(); ctx.moveTo(vp, hy); ctx.lineTo(vp + i*(w*0.06), h); ctx.stroke()
    }
    for (let i = 0; i < 12; i++) {
      const p = ((i / 12) + (t*0.15) % (1/12) * 12) % 1
      const y = hy + Math.pow(p, 2.2) * (h - hy)
      ctx.globalAlpha = 1 - p*0.7
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke()
    }
    ctx.globalAlpha = 1
  },
}

/* ================================================================
   CEREMONY VIEW — reveal panel + standings
================================================================ */
function CeremonyView({ data }) {
  const isPublicReveal = data.show?.phase === 'public_reveal'
  const jury = data.current_jury
  const step = data.state?.ceremony_step ?? -1
  const votes = jury?.votes ?? []

  let reveal = null
  if (isPublicReveal && data.current_public_reveal) {
    const r = data.current_public_reveal
    reveal = {
      entry_id: r.entry_id,
      team_name: r.team_name,
      team_code: r.team_code,
      team_flag_colors: r.team_flag_colors,
      team_logo_url: r.team_logo_url,
      entry_title: r.entry_title,
      points: r.points,
    }
  } else {
    reveal = (step > 0 && step <= votes.length) ? votes[step - 1] : null
  }

  return (
    <div className="h-full w-full grid" style={{ gridTemplateColumns: '1.4fr 1fr', background: '#2a1f4a' }}>
      <div className="relative overflow-hidden" style={{ background: 'var(--stage-bg)' }}>
        {/* Jury header */}
        <div className="p-8 border-b" style={{ borderColor: '#2a1f4a', background: 'linear-gradient(180deg,#120827,transparent)' }}>
          <div className="text-xs uppercase mb-2" style={{ color: isPublicReveal ? 'var(--stage-gold)' : 'var(--stage-cold)', letterSpacing: '.24em', fontWeight: 600 }}>
            {isPublicReveal ? 'Punten van het publiek' : 'Points from the jury of'}
          </div>
          <h1 style={{
            fontFamily: "'Big Shoulders Display', Impact, sans-serif",
            fontWeight: 900, fontSize: 'clamp(36px, 4.5vw, 72px)',
            textTransform: 'uppercase', letterSpacing: '-.01em', lineHeight: 1, margin: 0,
          }}>{isPublicReveal ? 'Publiek' : (jury?.name ?? '—')}</h1>
          {jury?.spokesperson && (
            <div className="mt-2 text-sm" style={{ color: '#8b7fa8' }}>Spokesperson: {jury.spokesperson}</div>
          )}
        </div>
        {/* Reveal stage */}
        <div className="relative" style={{ height: 'calc(100% - 200px)' }}>
          {!reveal && (
            <div className="absolute inset-0 flex flex-col justify-center items-start p-12 gap-3">
              <div className="text-sm uppercase" style={{ color: '#c9bde3', letterSpacing: '.16em', fontWeight: 600 }}>Awaiting next point</div>
              <div style={{
                fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 900, fontSize: 96,
                color: '#3d2b6b', lineHeight: 0.9,
              }}>
                {step}/{votes.length || POINT_STEPS.length}
              </div>
              <div className="text-sm" style={{ color: '#8b7fa8', letterSpacing: '.1em' }}>
                {votes.length === 0 ? 'Jury nog niet ingediend' : (step === 0 ? 'Klaar voor de eerste reveal — druk Next in control' : 'Klaar voor de volgende reveal')}
              </div>
            </div>
          )}
          {reveal && <RevealPanel reveal={reveal} isDouze={reveal.points === 12} />}
        </div>
      </div>
      {/* Standings */}
      <StandingsPanel standings={data.standings} highlightEntryId={reveal?.entry_id} douzeHighlight={reveal?.points === 12} />
    </div>
  )
}

function RevealPanel({ reveal, isDouze }) {
  const flag = parseFlag(reveal.team_flag_colors)
  const [score, setScore] = useState(0)
  useEffect(() => {
    setScore(0)
    const start = performance.now()
    let raf
    const step = (now) => {
      const p = Math.min(1, (now - start) / 500)
      const eased = 1 - Math.pow(1 - p, 3)
      setScore(Math.round(reveal.points * eased))
      if (p < 1) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [reveal.entry_id, reveal.points])

  return (
    <div className="absolute inset-0 flex flex-col justify-center p-12 gap-4" style={{ animation: 'revealSlide .35s cubic-bezier(.2,.7,.2,1)' }}>
      <style>{`@keyframes revealSlide { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: none; } }`}</style>
      {/* Flag swipe bg */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ opacity: 0.12, zIndex: 0 }}>
        {(flag ?? ['#666','#999','#ccc']).map((c, i) => (
          <div key={i} style={{
            position: 'absolute', top: '-30%', bottom: '-30%', width: '40%',
            left: `${-30 + i*40}%`, background: c, transform: 'skewX(-14deg)',
          }} />
        ))}
      </div>
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div className="font-mono text-xs mb-2" style={{ color: '#8b7fa8', letterSpacing: '.3em' }}>
          {reveal.team_code || '—'}
        </div>
        <h1 style={{
          fontFamily: "'Big Shoulders Display', Impact, sans-serif",
          fontWeight: 900, fontSize: 'clamp(56px, 8vw, 130px)', lineHeight: .88,
          textTransform: 'uppercase', letterSpacing: '-.02em', margin: 0,
          color: isDouze ? 'var(--stage-gold)' : '#f0e8ff',
        }}>{reveal.team_name}</h1>
        <div className="text-xl mt-1" style={{ color: '#c9bde3' }}>{reveal.entry_title}</div>
        <div className="flex items-baseline gap-6 mt-6">
          <div style={{
            fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 900,
            fontSize: 'clamp(72px, 11vw, 160px)', lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            color: isDouze ? 'var(--stage-gold)' : 'var(--stage-cold)',
          }}>{score}</div>
          <div style={{ fontWeight: 700, fontSize: 22, letterSpacing: '.18em', textTransform: 'uppercase', color: '#c9bde3' }}>
            Points
          </div>
        </div>
        {isDouze && (
          <div className="mt-4 inline-block px-3 py-1" style={{
            background: 'var(--stage-gold)', color: 'var(--stage-bg)', fontFamily: "'Big Shoulders Display', sans-serif",
            fontWeight: 900, fontSize: 20, letterSpacing: '.12em', textTransform: 'uppercase',
          }}>Douze points!</div>
        )}
      </div>
    </div>
  )
}

function StandingsPanel({ standings, highlightEntryId, douzeHighlight }) {
  return (
    <div className="flex flex-col overflow-hidden" style={{ background: '#120827' }}>
      <div className="p-6 border-b flex items-baseline justify-between" style={{ borderColor: '#2a1f4a' }}>
        <h3 style={{
          fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 800,
          fontSize: 22, textTransform: 'uppercase', letterSpacing: '.04em', margin: 0,
        }}>Current standings</h3>
        <span className="font-mono text-xs" style={{ color: '#8b7fa8', letterSpacing: '.1em' }}>Live</span>
      </div>
      <div className="flex-1 p-3 overflow-auto flex flex-col gap-1">
        {standings.map((s, i) => {
          const isLeader = i === 0
          const isHighlight = highlightEntryId === s.entry_id
          const flag = parseFlag(s.team_flag_colors)
          return (
            <div key={s.entry_id} className="flex items-center gap-3 p-2.5 rounded" style={{
              background: isHighlight ? (douzeHighlight ? 'linear-gradient(90deg, rgba(255,207,64,.22), #1a0f36 60%)' : 'linear-gradient(90deg, rgba(0,217,255,.16), #1a0f36 60%)') :
                          isLeader ? 'linear-gradient(90deg, rgba(255,207,64,.14), #1a0f36 40%)' : '#1a0f36',
              border: '1px solid ' + (isHighlight ? (douzeHighlight ? 'var(--stage-gold)' : 'var(--stage-cold)') : isLeader ? 'var(--stage-gold)' : 'transparent'),
              transition: 'all .3s ease',
            }}>
              <span className="w-8 text-center" style={{
                fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 800, fontSize: 22,
                color: isLeader ? 'var(--stage-gold)' : '#c9bde3', fontVariantNumeric: 'tabular-nums',
              }}>{i+1}</span>
              <TeamMark flag={flag} logo={s.team_logo_url} size="sm" />
              <span className="flex-1 truncate" style={{
                fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 800, fontSize: 18,
                textTransform: 'uppercase', letterSpacing: '.01em',
              }}>{s.team_name}</span>
              <span style={{
                fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 20,
                fontVariantNumeric: 'tabular-nums', color: '#f0e8ff',
              }}>{s.total_points}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ================================================================
   RESULTS VIEW — winner podium + confetti (Fase 8)
================================================================ */
function ResultsView({ data }) {
  const s = data.standings ?? []
  if (s.length === 0) {
    return <IdleScreen show={data.show} msg="Nog geen stemmen om te tonen" />
  }
  const winner = s[0]
  const second = s[1]
  const third  = s[2]
  const rest   = s.slice(3)

  return (
    <div className="h-full w-full relative overflow-hidden">
      <ConfettiOverlay />
      <FireworksOverlay />
      <div className="relative h-full w-full flex flex-col items-center p-6 gap-6 overflow-auto" style={{ zIndex: 1 }}>
        <div className="text-center pt-4">
          <div className="text-xs uppercase font-semibold" style={{ color: 'var(--stage-cold)', letterSpacing: '.32em' }}>
            De uitslag
          </div>
          <h1 style={{
            fontFamily: "'Big Shoulders Display', Impact, sans-serif",
            fontWeight: 900, fontSize: 'clamp(40px, 5vw, 84px)',
            textTransform: 'uppercase', letterSpacing: '-.01em', lineHeight: 1, margin: '4px 0 0',
          }}>{data.show.name}{data.show.edition ? ` — ${data.show.edition}` : ''}</h1>
        </div>

        {/* Podium */}
        <div className="flex items-end gap-4 md:gap-8 justify-center pt-4">
          {second && <PodiumBlock rank={2} entry={second} height={200} />}
          {winner && <PodiumBlock rank={1} entry={winner} height={280} isWinner />}
          {third  && <PodiumBlock rank={3} entry={third}  height={150} />}
        </div>

        {/* Rest */}
        {rest.length > 0 && (
          <div className="w-full max-w-2xl mt-6">
            <div className="text-xs uppercase mb-2" style={{ color: '#8b7fa8', letterSpacing: '.24em' }}>
              Verdere plaatsen
            </div>
            <div className="space-y-1">
              {rest.map((s, i) => (
                <div key={s.entry_id} className="flex items-center gap-3 p-3 rounded" style={{ background: '#1a0f36' }}>
                  <span className="w-8 text-center font-mono font-bold" style={{ color: '#c9bde3' }}>{i+4}</span>
                  <TeamMark flag={parseFlag(s.team_flag_colors)} logo={s.team_logo_url} size="sm" />
                  <span className="flex-1" style={{
                    fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 800, fontSize: 18,
                    textTransform: 'uppercase', letterSpacing: '.02em',
                  }}>{s.team_name}</span>
                  <span className="font-mono font-bold text-lg tabular-nums">{s.total_points}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function PodiumBlock({ rank, entry, height, isWinner }) {
  const flag = parseFlag(entry.team_flag_colors)
  const rankColor = {
    1: { grad: 'linear-gradient(180deg,var(--stage-gold) 0%,#ff9500 100%)', glow: '0 0 60px rgba(255,207,64,.55)' },
    2: { grad: 'linear-gradient(180deg,#dbe0e8 0%,#7a8a9c 100%)', glow: 'none' },
    3: { grad: 'linear-gradient(180deg,#d99155 0%,#8b4513 100%)', glow: 'none' },
  }[rank]
  return (
    <div className="flex flex-col items-center gap-3" style={{ width: isWinner ? 280 : 220 }}>
      {isWinner && (
        <div style={{
          fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 900, fontSize: 22,
          letterSpacing: '.28em', color: 'var(--stage-gold)', textTransform: 'uppercase',
          textShadow: '0 0 20px rgba(255,207,64,.6)',
        }}><span className="inline-flex items-center gap-2"><Trophy className="h-6 w-6" />Winnaar</span></div>
      )}
      <TeamMark flag={flag} logo={entry.team_logo_url} size="lg" />
      <div className="text-center min-h-[80px]">
        <div style={{
          fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 900,
          fontSize: isWinner ? 40 : 26, letterSpacing: '-.01em', lineHeight: 1,
          textTransform: 'uppercase', textWrap: 'balance',
          color: isWinner ? 'var(--stage-gold)' : '#f0e8ff',
        }}>{entry.team_name}</div>
        <div className="mt-1 text-sm" style={{ color: '#c9bde3' }}>{entry.entry_title}</div>
      </div>
      <div className="relative flex flex-col items-center justify-end" style={{
        width: '100%', height, background: rankColor.grad,
        borderRadius: '8px 8px 0 0', boxShadow: rankColor.glow,
        animation: isWinner ? 'podiumRise .8s cubic-bezier(.2,.7,.2,1)' : 'podiumRise 1s cubic-bezier(.2,.7,.2,1) both',
      }}>
        <style>{`@keyframes podiumRise { from { transform: translateY(20%); opacity: 0; } to { transform: none; opacity: 1; } }`}</style>
        <div className="p-4 text-center">
          <div style={{
            fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 900,
            fontSize: 72, color: 'rgba(0,0,0,.55)', lineHeight: 1,
          }}>{rank}</div>
          <div style={{
            fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 28,
            color: 'var(--stage-bg)', fontVariantNumeric: 'tabular-nums',
          }}>{entry.total_points}</div>
        </div>
      </div>
    </div>
  )
}

function ConfettiOverlay() {
  const ref = useRef(null)
  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    const fit = () => {
      const r = canvas.getBoundingClientRect()
      canvas.width  = Math.max(2, Math.floor(r.width * dpr))
      canvas.height = Math.max(2, Math.floor(r.height * dpr))
    }
    fit()
    const ro = new ResizeObserver(fit); ro.observe(canvas)

    const colors = ['var(--stage-hot)','var(--stage-cold)','var(--stage-gold)','var(--stage-purple)','var(--stage-pink)','#ffffff']
    const parts = []
    const spawn = (burst = false) => {
      const w = canvas.width
      const count = burst ? 40 : 1
      for (let i = 0; i < count; i++) {
        parts.push({
          x: Math.random() * w,
          y: burst ? -20 : -20 - Math.random() * 40,
          vx: (Math.random() - 0.5) * 240 * dpr,
          vy: (80 + Math.random() * 180) * dpr,
          rot: Math.random() * Math.PI * 2,
          vrot: (Math.random() - 0.5) * 10,
          w: (6 + Math.random() * 8) * dpr,
          h: (4 + Math.random() * 4) * dpr,
          color: colors[(Math.random() * colors.length) | 0],
          life: 3.5 + Math.random() * 3,
        })
      }
    }
    // Initial burst so it feels celebratory right away
    for (let i = 0; i < 6; i++) spawn(true)

    let last = performance.now(), raf, spawnAcc = 0
    const loop = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000)
      last = now
      spawnAcc += dt
      while (spawnAcc > 0.04) { spawnAcc -= 0.04; spawn() }

      ctx.clearRect(0, 0, canvas.width, canvas.height)
      for (let i = parts.length - 1; i >= 0; i--) {
        const p = parts[i]
        p.life -= dt
        if (p.life <= 0 || p.y > canvas.height + 60) { parts.splice(i, 1); continue }
        p.vy += 340 * dpr * dt
        p.x += p.vx * dt
        p.y += p.vy * dt
        p.rot += p.vrot * dt
        ctx.save()
        ctx.translate(p.x, p.y)
        ctx.rotate(p.rot)
        ctx.fillStyle = p.color
        ctx.globalAlpha = Math.min(1, p.life)
        ctx.fillRect(-p.w/2, -p.h/2, p.w, p.h)
        ctx.restore()
      }
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => { cancelAnimationFrame(raf); ro.disconnect() }
  }, [])
  return <canvas ref={ref} className="fixed inset-0 pointer-events-none" style={{ zIndex: 50, width: "100vw", height: "100vh" }} />
}

/* ================================================================
   FireworksOverlay — raketten die exploderen in particles
   Fixed full-viewport canvas boven confetti
================================================================ */
function FireworksOverlay() {
  const ref = useRef(null)
  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    const fit = () => {
      const r = canvas.getBoundingClientRect()
      canvas.width  = Math.max(2, Math.floor(r.width * dpr))
      canvas.height = Math.max(2, Math.floor(r.height * dpr))
    }
    fit()
    const ro = new ResizeObserver(fit); ro.observe(canvas)

    const PALETTES = [
      ['var(--stage-hot)','var(--stage-pink)','#ffffff'],   // magenta / pink / white
      ['var(--stage-cold)','var(--stage-blue)','#ffffff'],   // cyan / blue / white
      ['var(--stage-gold)','#ff9500','#ffffff'],   // gold / orange / white
      ['var(--stage-purple)','var(--stage-lime)','#ffffff'],   // purple / lime / white
      ['var(--stage-red)','var(--stage-gold)','#ffffff'],   // red / yellow / white
    ]

    const rockets = []   // { x, y, vx, vy, life, colors, exploded }
    const particles = [] // { x, y, vx, vy, life, maxLife, color, size }
    const trails = []    // { x, y, life, color }

    function launchRocket() {
      const w = canvas.width, h = canvas.height
      const startX = (0.15 + Math.random() * 0.7) * w
      const targetY = (0.15 + Math.random() * 0.4) * h
      const dy = targetY - h
      // Time to reach: use gravity 500*dpr → v0 = sqrt(2*g*d) approximation
      const flightTime = 1.0 + Math.random() * 0.4
      const g = 400 * dpr
      const vy = (dy - 0.5 * g * flightTime * flightTime) / flightTime
      const vx = (Math.random() - 0.5) * 40 * dpr
      rockets.push({
        x: startX, y: h, vx, vy,
        life: flightTime,
        colors: PALETTES[(Math.random() * PALETTES.length) | 0],
        exploded: false,
      })
    }

    function explode(rocket) {
      const count = 50 + Math.floor(Math.random() * 40)
      const speedBase = (2 + Math.random() * 2) * 60 * dpr
      for (let i = 0; i < count; i++) {
        const ang = (i / count) * Math.PI * 2 + Math.random() * 0.05
        const speed = speedBase * (0.5 + Math.random() * 0.6)
        particles.push({
          x: rocket.x,
          y: rocket.y,
          vx: Math.cos(ang) * speed,
          vy: Math.sin(ang) * speed,
          life: 1.5 + Math.random() * 1.2,
          maxLife: 1.5,
          color: rocket.colors[(Math.random() * rocket.colors.length) | 0],
          size: (2 + Math.random() * 2) * dpr,
        })
      }
    }

    let last = performance.now(), raf, nextLaunch = 0
    const loop = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000)
      last = now
      nextLaunch -= dt

      if (nextLaunch <= 0) {
        launchRocket()
        // Sometimes 2 rockets close together
        if (Math.random() < 0.4) setTimeout(launchRocket, 150)
        nextLaunch = 0.8 + Math.random() * 1.4
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Rockets
      const g = 400 * dpr
      for (let i = rockets.length - 1; i >= 0; i--) {
        const r = rockets[i]
        r.life -= dt
        r.vy += g * dt
        r.x += r.vx * dt
        r.y += r.vy * dt
        // Trail
        ctx.fillStyle = r.colors[0]
        ctx.globalAlpha = 0.8
        ctx.beginPath()
        ctx.arc(r.x, r.y, 2.5 * dpr, 0, Math.PI * 2)
        ctx.fill()
        // Explode when apex or life out
        if (r.vy > -20 * dpr || r.life <= 0) {
          explode(r)
          rockets.splice(i, 1)
        }
      }

      // Particles
      ctx.globalAlpha = 1
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i]
        p.life -= dt
        if (p.life <= 0) { particles.splice(i, 1); continue }
        p.vy += g * 0.4 * dt   // lighter gravity for prettier tail
        p.vx *= 0.985         // air drag
        p.vy *= 0.985
        p.x += p.vx * dt
        p.y += p.vy * dt
        const alpha = Math.max(0, Math.min(1, p.life / p.maxLife))
        ctx.fillStyle = p.color
        ctx.globalAlpha = alpha
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.globalAlpha = 1
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => { cancelAnimationFrame(raf); ro.disconnect() }
  }, [])
  return <canvas ref={ref} className="fixed inset-0 pointer-events-none" style={{ zIndex: 1, width: "100vw", height: "100vh" }} />
}

/* ================================================================
   WINNER AUDIO — losse audio-tag voor results-view. Speelt de winnaar
   z'n eigen bestand, gestuurd via `audio_playing` in de show-state.
   Geen Web Audio-visualizer nodig (results-scherm heeft podium + confetti,
   geen visualizer). Zonder autoplay — vereist een user-gesture, wat door de
   Play-knop in de control-page geleverd wordt.
================================================================ */
function WinnerAudio({ url, shouldPlay }) {
  const audioRef = useRef(null)
  // Zet de src bij url-wijziging expliciet + load(), anders houdt Safari soms
  // de vorige buffer vast.
  useEffect(() => {
    const a = audioRef.current
    if (!a || !url) return
    if (a.src !== url) {
      a.src = url
      a.load()
    }
  }, [url])
  // Play/pause volgens shouldPlay-flag.
  useEffect(() => {
    const a = audioRef.current
    if (!a || !url) return
    if (shouldPlay) {
      a.play().catch(err => console.warn('[WinnerAudio] play() geweigerd:', err?.name, err?.message))
    } else {
      a.pause()
    }
  }, [shouldPlay, url])
  if (!url) return null
  return <audio ref={audioRef} preload="auto" />
}
